import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { chauffeurCategoryOptions, chauffeurFormatOptions } from '@/mocks/corporateChauffeurData';

import { submitContact } from '@/lib/contactApi';

export default function ContactForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company: '',
    department: '',
    name: '',
    email: '',
    phone: '',
    category: '',
    format: '',
    participants: '',
    timeline: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('corporateChauffeur.contact.nameError');
    if (!formData.email.trim()) {
      newErrors.email = t('corporateChauffeur.contact.emailError');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('corporateChauffeur.contact.emailInvalidError');
    }
    if (!formData.phone.trim()) newErrors.phone = t('corporateChauffeur.contact.phoneError');
    if (!formData.category) newErrors.category = t('corporateChauffeur.contact.categoryError');
    if (formData.message.length > 500) {
      newErrors.message = t('corporateChauffeur.contact.messageMax');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    try {
      await submitContact('corporate-chauffeur', { ...formData, industry: 'ハイヤー・送迎' });

      navigate('/corporate/chauffeur/complete');
    } catch {
      setErrors({ submit: t('corporateChauffeur.contact.submitError') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/50" id="contact-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          {String(t('corporateChauffeur.contact.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporateChauffeur.contact.title')}
          </h2>
          {String(t('corporateChauffeur.contact.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto mb-5"></div>
          {String(t('corporateChauffeur.contact.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed">{__s}</p>))}
        </div>

        <div className="text-center mb-10">
          <p className="text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
            {t('corporateChauffeur.contact.phoneText')}{' '}
            <a href="tel:0368244086" className="text-foreground-700 font-medium hover:text-accent-600 transition-colors">
              03-6824-4086
            </a>
            {' '}{t('corporateChauffeur.contact.phoneOr')}{' '}
            <a href="mailto:info@butler-academy.jp" className="text-accent-600 hover:text-accent-500 transition-colors">
              info@butler-academy.jp
            </a>
            {' '}{t('corporateChauffeur.contact.phoneAccept')}
          </p>
          {String(t('corporateChauffeur.contact.hours')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 mt-1.5 tracking-[0.03em]">{__s}</p>))}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.company')}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.companyPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.department')}
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.departmentPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.name')} <span className="text-accent-600 text-[12px]">{t('corporateChauffeur.contact.nameRequired')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.namePlaceholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.email')} <span className="text-accent-600 text-[12px]">{t('corporateChauffeur.contact.emailRequired')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.phone')} <span className="text-accent-600 text-[12px]">{t('corporateChauffeur.contact.phoneRequired')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.phonePlaceholder')}
              />
              {errors.phone && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.category')} <span className="text-accent-600 text-[12px]">{t('corporateChauffeur.contact.categoryRequired')}</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                {chauffeurCategoryOptions.map((opt, idx) => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                    {t(`corporateChauffeur.contact.catOpt${idx}`)}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.category}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.format')}
              </label>
              <select
                name="format"
                value={formData.format}
                onChange={(e) => handleChange('format', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                {chauffeurFormatOptions.map((opt, idx) => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                    {t(`corporateChauffeur.contact.fmtOpt${idx}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.participants')}
              </label>
              <input
                type="text"
                name="participants"
                value={formData.participants}
                onChange={(e) => handleChange('participants', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.participantsPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.timeline')}
              </label>
              <input
                type="text"
                name="timeline"
                value={formData.timeline}
                onChange={(e) => handleChange('timeline', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('corporateChauffeur.contact.timelinePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('corporateChauffeur.contact.message')}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('corporateChauffeur.contact.messagePlaceholder')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.message.length}/500
              </p>
              {errors.message && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.message}</p>
              )}
            </div>

            {errors.submit && (
              <p className="text-[14px] text-red-500 text-center tracking-[0.04em]">{errors.submit}</p>
            )}

            <div className="pt-3">
              <button
                type="submit"
                disabled={submitting}
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t('corporateChauffeur.contact.submitting') : t('corporateChauffeur.contact.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}